import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { connectState } from '@examdojo/angular/util';
import { ButtonComponent } from '@examdojo/core/button';
import { LoaderComponent } from '@examdojo/core/loader';
import { IfSmallDirective, IfWideLayoutDirective } from '@examdojo/layout';
import {
  provideLocalMcqSelection,
  provideSingleAttemptSelfGradingSelection,
  QuestionAttemptEntityQuery,
  QuestionAttemptEntityService,
} from '@examdojo/question';
import { FooterPanelComponent } from '@examdojo/ui/footer-panel';
import { ModalController } from '@ionic/angular/standalone';
import { TranslocoPipe } from '@jsverse/transloco';
import { catchError, EMPTY, finalize } from 'rxjs';
import { RootUrlParts } from '../../../../app.model';
import { ExamdojoQuestionComponent } from '../../../../question-v2/examdojo-question/examdojo-question.component';
import { START_PRACTICE_REDIRECT_TO_QUERY_PARAM } from '../../../practice/start-practice/start-practice.component';
import { QUESTION_BANK_ANSWER_REVIEW_DIALOG_ID } from './question-bank-answer-review-dialog.service';

@Component({
  selector: 'dojo-question-bank-answer-review-dialog',
  standalone: true,
  imports: [
    ExamdojoQuestionComponent,
    FooterPanelComponent,
    ButtonComponent,
    TranslocoPipe,
    LoaderComponent,
    NgClass,
    IfSmallDirective,
    IfWideLayoutDirective,
  ],
  providers: [provideLocalMcqSelection(), provideSingleAttemptSelfGradingSelection()],
  templateUrl: './question-bank-answer-review-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex flex-col h-full safe-area-top safe-area-bottom' },
})
export class QuestionBankAnswerReviewDialogComponent implements OnInit {
  private readonly questionAttemptEntityQuery = inject(QuestionAttemptEntityQuery);
  private readonly questionAttemptEntityService = inject(QuestionAttemptEntityService);
  private readonly modalController = inject(ModalController);
  private readonly router = inject(Router);

  readonly RootUrlParts = RootUrlParts;

  readonly questionId = input.required<string>();
  readonly isLoading = signal(true);
  readonly loadError = signal(false);

  readonly state = connectState({
    isQuestionMultipleChoice: this.questionAttemptEntityQuery.isQuestionMultipleChoice$,
  });

  private readonly startPracticeQueryParams = {
    [START_PRACTICE_REDIRECT_TO_QUERY_PARAM]: `/${RootUrlParts.QuestionBank}`,
  };

  ngOnInit(): void {
    this.loadAttempts();
  }

  closeDialog(): void {
    this.modalController.dismiss(undefined, undefined, QUESTION_BANK_ANSWER_REVIEW_DIALOG_ID);
  }

  navigateToTryAgain(): void {
    this.closeDialog();
    this.router.navigate(['/', this.RootUrlParts.StartPractice, this.questionId()], {
      queryParams: this.startPracticeQueryParams,
    });
  }

  onQuestionCompleted(action: 'continue' | 'stop'): void {
    if (action === 'stop') {
      this.closeDialog();
    }
  }

  private loadAttempts(): void {
    const questionId = Number(this.questionId());

    if (isNaN(questionId)) {
      this.loadError.set(true);
      this.isLoading.set(false);
      return;
    }

    this.questionAttemptEntityService
      .setLatestGradedAttemptAsActive(questionId)
      .pipe(
        catchError(() => {
          this.loadError.set(true);
          return EMPTY;
        }),
        finalize(() => {
          this.isLoading.set(false);
        }),
      )
      .subscribe();
  }
}
